import { Flex, Portal } from '@chakra-ui/react'
import { ArrowRightLeft } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { MenuPopoverWrapper } from '~/components/shared/Dropdown/Menu'
import QueueStatusMenu from '~/components/shared/Dropdown/QueueStatusMenu'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { Popover, PopoverAnchor } from '~/components/ui/popover'
import { isQueueAgentStatus, QueueAgentStatus } from '~/hooks/useCallQueue'
import { useQueueAlerts } from '~/hooks/useQueueToasts'
import { useUserClickedExternalElements } from '~/hooks/useUserClickedExternalElements'
import {
  useQueueLogin,
  useQueueLogout,
  useUpdateUserAgentStatus,
} from '~/queries/queueQueries'
import { AgentCustomStatus } from '~/types/AgentCustomStatus'
import { Size } from '~/types/ThemeTypes'

import { ApiError, ExtensionPreview } from '@mangovoice/types'

const AgentStatusActionsCellContent = ({
  customStatuses,
  selectedValue,
  extension,
  onStatusUpdated,
}: {
  customStatuses: AgentCustomStatus[]
  selectedValue: QueueAgentStatus | string
  extension?: ExtensionPreview
  onStatusUpdated?: () => void
}) => {
  const { t } = useTranslation()

  const params = {
    extensionId: extension?.id,
    options: {
      retry: 0,
    },
  }
  const {
    showQueueUpdateErrorModal,
    showQueueUpdateGenericErrorModal,
    showStatusUpdatedToast,
  } = useQueueAlerts()
  const queueLogin = useQueueLogin(params)
  const queueLogout = useQueueLogout(params)
  const updateUserAgentStatus = useUpdateUserAgentStatus({})

  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)

  const handleQueueError = (error: unknown) => {
    if ((error as ApiError)?.status === 404) {
      showQueueUpdateErrorModal()
    } else {
      showQueueUpdateGenericErrorModal()
    }
  }

  const handleStatusChange = async (value: QueueAgentStatus | string) => {
    switch (value) {
      case QueueAgentStatus.InQueue:
        try {
          await queueLogin()
          await updateUserAgentStatus(null, extension?.uuid, true)
          showStatusUpdatedToast()
          onStatusUpdated?.()
        } catch (error: unknown) {
          handleQueueError(error)
        }
        break
      case QueueAgentStatus.Offline:
        try {
          await queueLogout()
          await updateUserAgentStatus(null, extension?.uuid, false)
          showStatusUpdatedToast()
          onStatusUpdated?.()
        } catch (error: unknown) {
          handleQueueError(error)
        }
        break
      default:
        try {
          await queueLogout()
          if (!isQueueAgentStatus(value)) {
            await updateUserAgentStatus(value, extension?.uuid, false)
            showStatusUpdatedToast()
            onStatusUpdated?.()
          }
        } catch (error: unknown) {
          handleQueueError(error)
        }
        break
    }
  }

  const handleSelect = (value: QueueAgentStatus | string) => {
    void handleStatusChange(value)
  }

  useUserClickedExternalElements(
    [containerRef.current, buttonRef.current],
    true,
    () => {
      if (menuOpen) {
        setMenuOpen(false)
      }
    },
  )

  return (
    <Flex alignItems="center" gap="0.5rem" alignSelf="stretch">
      <Popover open={menuOpen} positioning={{ placement: 'bottom-start' }}>
        <PopoverAnchor>
          <QuickActionButton
            ref={buttonRef}
            tooltipDisabled={menuOpen}
            variant={QuickActionButtonVariants.Default_100}
            actionIcon={ArrowRightLeft}
            tooltipText={t('agentStatusActions.tooltip.changeStatus')}
            size={Size.sm}
            onClick={() => setMenuOpen((prev) => !prev)}
          />
        </PopoverAnchor>
        <Portal>
          {menuOpen && (
            <MenuPopoverWrapper ref={containerRef}>
              <QueueStatusMenu
                customStatuses={customStatuses ?? []}
                selectedValue={selectedValue}
                handleSelect={(value) => {
                  setMenuOpen(false)
                  handleSelect(value)
                }}
              />
            </MenuPopoverWrapper>
          )}
        </Portal>
      </Popover>
    </Flex>
  )
}

export default AgentStatusActionsCellContent
